import { CSSProperties, MouseEvent, ReactNode } from "react";
import { playSfx } from "../logic/sfx";
import { defaultBtn, primaryBtn } from "../theme/styles";
import { pixelTheme } from "../theme/pixel";

// Lightweight button wrapper that:
// - Visually disables (low opacity, not-allowed cursor) without
//   setting the HTML `disabled` attribute, so clicks still fire.
// - Plays the notice / error sound when clicked while disabled,
//   instead of silently dropping the click.
//
// Use this anywhere a button has a "you can't click this right
// now, but here's a reason" UX (cooldowns, validation locks, etc).
// The standard `disabled={true}` `<button>` is still appropriate
// when there's truly nothing to communicate to the user (e.g. the
// "next page" button when already on the last page).

interface DoiButtonProps {
  variant?: "primary" | "default";
  disabled?: boolean;
  /** Sound to play when clicked while disabled. Defaults to the
   *  notice / warning sound. */
  disabledSfx?: "ai_notice_or_warning_sound" | "ai_error_sound";
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
  style?: CSSProperties;
  title?: string;
}

export function DoiButton({
  variant = "default",
  disabled = false,
  disabledSfx = "ai_notice_or_warning_sound",
  onClick,
  children,
  style,
  title,
}: DoiButtonProps) {
  const theme = pixelTheme;
  const props =
    variant === "primary" ? primaryBtn(theme, disabled) : defaultBtn(theme, disabled);

  return (
    <button
      type="button"
      title={title}
      aria-disabled={disabled || undefined}
      className={props.className}
      style={{ ...props.style, ...style }}
      onClick={(e) => {
        if (disabled) {
          playSfx(disabledSfx);
          return;
        }
        onClick(e);
      }}
    >
      {children}
    </button>
  );
}
